/**
 * BAĞLANTI DURUMU BANDI — Slack/VS Code'daki "Connecting..." deseni.
 * İki bağımsız sinyal izlenir:
 *  1. Tarayıcı çevrimdışı (navigator.onLine + online/offline olayları) → kırmızı bant.
 *  2. İnternet var ama API sunucusuna ulaşılamıyor (healthz ping) → sarı "yeniden
 *     bağlanılıyor" bandı. Ping NORMALDE seyrek (45 sn); sorun görülünce 5 sn'ye iner.
 * Bağlantı geri gelince 2.5 sn yeşil "bağlantı kuruldu" gösterip kaybolur.
 */
import { useEffect, useRef, useState } from "react";
import { WifiOff, RefreshCw, Check } from "lucide-react";

type Durum = "yok" | "cevrimdisi" | "sunucu-yok" | "geri-geldi";

export function ConnectionBanner() {
  const [durum, setDurum] = useState<Durum>("yok");
  const durumRef = useRef(durum);
  durumRef.current = durum;

  useEffect(() => {
    let kapandi = false;
    let zamanlayici: number | undefined;
    let geriGeldiZamanlayici: number | undefined;

    const geriGeldi = () => {
      setDurum("geri-geldi");
      window.clearTimeout(geriGeldiZamanlayici);
      geriGeldiZamanlayici = window.setTimeout(() => { if (!kapandi) setDurum("yok"); }, 2500);
    };

    const pingle = async () => {
      if (kapandi) return;
      if (!navigator.onLine) { planla(5000); return; } // çevrimdışı bandı zaten açık
      try {
        const r = await fetch("/api/healthz", { signal: AbortSignal.timeout(6000), cache: "no-store" });
        if (!r.ok) throw new Error(String(r.status));
        if (durumRef.current === "sunucu-yok") geriGeldi();
        planla(45_000);
      } catch {
        if (!kapandi && navigator.onLine) setDurum("sunucu-yok");
        planla(5000); // sorun varken sık dene — bant "yeniden bağlanılıyor" derken gerçekten denesin
      }
    };
    const planla = (ms: number) => { window.clearTimeout(zamanlayici); zamanlayici = window.setTimeout(() => void pingle(), ms); };

    const cevrimdisiOldu = () => { setDurum("cevrimdisi"); planla(3000); };
    const cevrimiciOldu = () => { geriGeldi(); planla(500); }; // internet döndü — sunucuyu hemen yokla
    window.addEventListener("offline", cevrimdisiOldu);
    window.addEventListener("online", cevrimiciOldu);
    if (!navigator.onLine) setDurum("cevrimdisi");
    planla(45_000);

    return () => {
      kapandi = true;
      window.clearTimeout(zamanlayici);
      window.clearTimeout(geriGeldiZamanlayici);
      window.removeEventListener("offline", cevrimdisiOldu);
      window.removeEventListener("online", cevrimiciOldu);
    };
  }, []);

  if (durum === "yok") return null;

  const stil = durum === "cevrimdisi"
    ? { arka: "#d92d20", metin: "İnternet bağlantın yok — yaptığın değişiklikler kaydedilemeyebilir", Ikon: WifiOff, donsun: false }
    : durum === "sunucu-yok"
      ? { arka: "#b45309", metin: "Sunucuya bağlanılamıyor — yeniden bağlanılıyor…", Ikon: RefreshCw, donsun: true }
      : { arka: "#15803d", metin: "Bağlantı kuruldu", Ikon: Check, donsun: false };

  return (
    <div role="status" className="fixed top-0 left-0 right-0 z-[200] flex items-center justify-center gap-2 py-1.5 text-[12px] font-medium text-white shadow-md"
      style={{ background: stil.arka }}>
      <stil.Ikon className={`w-3.5 h-3.5 ${stil.donsun ? "animate-spin" : ""}`} />
      {stil.metin}
    </div>
  );
}
